class BrewPicProduct extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.busy = false;
    this.form = this.querySelector('form');
    this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'EUR' });
    this.addEventListener('click', event => {
      const thumb = event.target.closest('[data-gallery-image]');
      if (thumb) this.showImage(thumb);
    });
    this.addEventListener('change', event => {
      if (event.target.matches('[data-camera-select]')) {
        const option = event.target.selectedOptions[0];
        const thumb = Array.from(this.querySelectorAll('[data-photo]')).find(item => item.dataset.photo === option.dataset.colour);
        if (thumb) this.showImage(thumb);
      }
      this.update();
    });
    this.addEventListener('input', event => {
      if (event.target.matches('[data-addon-quantity]')) this.update();
    });
    if (this.form) this.form.addEventListener('submit', event => this.addToCart(event));
    this.update();
  }
  money(cents) { return this.formatter.format(cents / 100); }
  count() { return Number(this.querySelector('[data-bundle-count]:checked')?.value || 1); }
  options(count = this.count()) {
    return Array.from(this.querySelectorAll('[data-camera-select]')).slice(0, count).map(select => select.selectedOptions[0]);
  }
  saving(count) {
    return count === 3 ? Number(this.dataset.threeSaving) : count === 2 ? Number(this.dataset.twoSaving) : 0;
  }
  addonQuantity() {
    const value = Number(this.querySelector('[data-addon-quantity]').value);
    return Number.isInteger(value) && value >= 1 && value <= 10 ? value : 0;
  }
  update() {
    const count = this.count();
    this.querySelectorAll('[data-offer]').forEach(card => card.classList.toggle('is-selected', Number(card.dataset.offer) === count));
    this.querySelectorAll('[data-camera-row]').forEach(row => {
      row.hidden = Number(row.dataset.cameraRow) > count;
      row.querySelector('select').disabled = row.hidden || this.busy;
    });
    const selected = this.options();
    const addon = this.querySelector('[data-addon]');
    const addonActive = addon.checked && !addon.disabled;
    this.querySelector('[data-addon-quantity-row]').hidden = !addonActive;
    this.querySelector('[data-addon-quantity]').disabled = !addonActive || this.busy;
    for (let n = 1; n <= 3; n++) {
      const regular = this.options(n).reduce((sum, option) => sum + Number(option?.dataset.price || 0), 0);
      this.querySelector(`[data-offer-price="${n}"]`).textContent = this.money(Math.max(0, regular - this.saving(n)));
      const compare = this.options(n).reduce((sum, option) => sum + Number(option?.dataset.compare || option?.dataset.price || 0), 0);
      const savings = compare - regular + this.saving(n);
      this.querySelector(`[data-offer-regular="${n}"]`).textContent = this.money(compare);
      this.querySelector(`[data-offer-savings="${n}"]`).textContent = `${this.dataset.saveLabel} ${this.money(savings)} (${Math.round(savings / compare * 100)}%)`;
    }
    const cameras = selected.reduce((sum, option) => sum + Number(option?.dataset.price || 0), 0);
    const custom = this.querySelector('[data-custom-toggle]');
    const customActive = custom.checked && !custom.disabled;
    const fields = this.querySelector('[data-custom-inputs]');
    fields.hidden = !customActive;
    fields.querySelectorAll('input, textarea').forEach(input => input.disabled = !customActive || this.busy);
    const serviceExtra = Array.from(this.querySelectorAll('[data-service]:checked:not(:disabled)')).reduce((sum, input) => sum + Number(input.dataset.price) * (input.dataset.kind === 'care' ? count : 1), 0);
    const care = this.querySelector('[data-service][data-kind=care]');
    if (care) this.querySelector('[data-care-cost]').textContent = `${count} × ${this.money(Number(care.dataset.price))} = ${this.money(count * Number(care.dataset.price))}`;
    const extra = serviceExtra + (customActive ? Number(custom.dataset.price) : 0) + (addonActive ? Number(addon.dataset.price) * this.addonQuantity() : 0);
    this.querySelector('[data-order-total]').textContent = this.money(Math.max(0, cameras - this.saving(count)) + extra);
    this.querySelector('[data-product-price]').textContent = this.money(Number(selected[0]?.dataset.price || 0));
    const price = Number(selected[0]?.dataset.price || 0);
    const compare = Number(selected[0]?.dataset.compare || price);
    this.querySelector('[data-product-compare]').textContent = this.money(compare);
    this.querySelector('[data-product-saving]').textContent = `${Math.round((compare-price)/compare*100)}% OFF`;
    const available = selected.length === count && selected.every(option => option?.dataset.available === 'true');
    const button = this.querySelector('[data-add-button]');
    button.disabled = this.busy || !available || (addonActive && this.addonQuantity() === 0);
    button.textContent = this.busy ? button.dataset.busyLabel : available ? button.dataset.addLabel : button.dataset.soldOutLabel;
  }
  items() {
    const quantities = new Map();
    for (const option of this.options()) {
      if (!option || option.dataset.available !== 'true') throw new Error('Unavailable variant');
      quantities.set(option.value, (quantities.get(option.value) || 0) + 1);
    }
    const addon = this.querySelector('[data-addon]');
    if (addon.checked && !addon.disabled) {
      if (!addon.dataset.variant || !this.addonQuantity()) throw new Error('Invalid stencil quantity');
      quantities.set(addon.dataset.variant, this.addonQuantity());
    }
    this.querySelectorAll('[data-service]:checked:not(:disabled)').forEach(input => quantities.set(input.dataset.variant, input.dataset.kind === 'care' ? this.count() : 1));
    return Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
  }
  async addToCart(event) {
    event.preventDefault();
    if (this.busy || this.querySelector('[data-add-button]').disabled) return;
    const error = this.querySelector('[data-cart-error]');
    error.hidden = true;
    const controls = Array.from(this.form.querySelectorAll('input, select, textarea'));
    let disabledStates;
    try {
      validatePhotos(this.form);
      if (!this.form.reportValidity()) return;
      let items = this.items();
      const priority = this.querySelector('[data-service][data-kind=priority]');
      const priorityActive = priority?.checked && !priority.disabled;
      const custom = this.querySelector('[data-custom-toggle]');
      const customActive = custom.checked && !custom.disabled;
      disabledStates = controls.map(control => control.disabled);
      this.busy = true;
      controls.forEach(control => control.disabled = true);
      const submitButton = this.querySelector('[data-add-button]');
      submitButton.disabled = true;
      submitButton.textContent = submitButton.dataset.busyLabel;
      if (priorityActive) {
        const existingResponse = await fetch(`${this.dataset.root || '/'}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!existingResponse.ok) throw new Error('Could not check your cart. Please try again.');
        const existing = await existingResponse.json();
        if (existing.items.some(item => String(item.variant_id) === priority.dataset.variant)) items = items.filter(item => String(item.id) !== priority.dataset.variant);
      }
      const body = new FormData();
      items.forEach((item, index) => {
        body.append(`items[${index}][id]`, item.id);
        body.append(`items[${index}][quantity]`, item.quantity);
      });
      if (customActive) {
        const index = items.length;
        body.append(`items[${index}][id]`, custom.dataset.variant);
        body.append(`items[${index}][quantity]`, '1');
        this.querySelectorAll('[data-custom-inputs] [name]').forEach(input => {
          const name = input.name.replace(/^properties\[/, '').replace(/\]$/, '');
          body.append(`items[${index}][properties][${name}]`, input.type === 'file' ? input.files[0] : input.value);
        });
      }
      await document.querySelector('brewpic-cart-drawer').mutate('add', body);
    } catch (cause) {
      error.textContent = cause.message;
      error.hidden = false;
    } finally {
      if (disabledStates) controls.forEach((control, index) => control.disabled = disabledStates[index]);
      this.busy = false;
      this.update();
    }
  }

  showImage(thumb) {
    const main = this.querySelector('[data-main-image]');
    if (!main) return;
    main.src = thumb.dataset.galleryImage;
    main.removeAttribute('srcset');
    main.alt = thumb.dataset.galleryAlt;
    this.querySelectorAll('[data-gallery-image]').forEach(item => {
      item.classList.toggle('is-active', item === thumb);
      item.setAttribute('aria-pressed', String(item === thumb));
    });
  }
}
if (!customElements.get('brewpic-product')) customElements.define('brewpic-product', BrewPicProduct);

class BrewPicDemo extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    const image = this.querySelector('img');
    const button = this.querySelector('button');
    let playing = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const update = () => {
      image.src = playing ? image.dataset.animation : image.dataset.poster;
      button.textContent = playing ? button.dataset.pause : button.dataset.play;
      button.setAttribute('aria-pressed', String(playing));
    };
    button.addEventListener('click', () => { playing = !playing; update(); });
    update();
  }
}
if (!customElements.get('brewpic-demo')) customElements.define('brewpic-demo', BrewPicDemo);

function validatePhotos(form) {
  for (const input of form.querySelectorAll('[data-custom-file]:not(:disabled)')) {
    input.setCustomValidity('');
    const file = input.files[0];
    if (!file || !['image/jpeg', 'image/png'].includes(file.type) || file.size > 10 * 1024 * 1024) {
      input.setCustomValidity('Choose a JPG or PNG photo up to 10 MB.');
      input.reportValidity();
      throw new Error('Please upload all five photos as JPG or PNG files, up to 10 MB each.');
    }
  }
}
document.addEventListener('change', event => {
  if (event.target.matches('[data-custom-file]')) event.target.setCustomValidity('');
});
class BrewPicCartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.dialog = this.querySelector('dialog');
    this.dialog.addEventListener('close', () => { document.documentElement.classList.remove('bp-cart-open'); this.opener?.focus(); });
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
    this.addEventListener('click', async event => {
      if (event.target.closest('[data-close-cart]')) this.dialog.close();
      const change = event.target.closest('[data-cart-change]');
      const extra = event.target.closest('[data-cart-extra]');
      if ((!change && !extra) || this.busy) return;
      try {
        await this.mutate(change ? 'change' : 'add', change ? { id: change.dataset.key, quantity: Number(change.dataset.quantity) } : { items: [{ id: extra.dataset.cartExtra, quantity: 1 }] });
      } catch (error) { this.error(error.message); }
    });
    document.addEventListener('click', async event => {
      if (!event.target.closest('[data-open-cart]')) return;
      event.preventDefault(); this.open();
      if (this.busy) return;
      try { await this.refresh(); } catch (error) { this.error(this.dataset.error); }
    });
  }
  open() {
    if (this.dialog.open) return;
    this.opener = document.activeElement;
    this.dialog.showModal();
    document.documentElement.classList.add('bp-cart-open');
  }
  error(message) {
    const error = this.querySelector('[data-drawer-error]');
    error.textContent = message; error.hidden = false;
    this.querySelector('[data-drawer-recovery]').hidden = false;
  }
  render(html) {
    const parsed = new DOMParser().parseFromString(html || '', 'text/html');
    const content = parsed.querySelector('[data-drawer-content]');
    if (!content) throw new Error(this.dataset.error);
    this.querySelector('[data-drawer-content]').replaceWith(content);
    document.querySelectorAll('[data-cart-count]').forEach(element => element.textContent = content.dataset.count);
    this.querySelector('[data-drawer-error]').hidden = true;
    this.querySelector('[data-drawer-recovery]').hidden = true;
  }
  async refresh() {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.dataset.sectionId);
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(this.dataset.error);
    const sections = await response.json();
    this.render(sections[this.dataset.sectionId]);
  }
  async mutate(action, payload) {
    if (this.busy) throw new Error('Please wait for the cart to finish updating.');
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    this.querySelectorAll('button').forEach(button => button.disabled = true);
    let added = false;
    try {
      const multipart = payload instanceof FormData;
      if (multipart) { payload.set('sections', this.dataset.sectionId); payload.set('sections_url', window.location.pathname); }
      else { payload.sections = this.dataset.sectionId; payload.sections_url = window.location.pathname; }
      const response = await fetch(`${this.dataset.root || '/'}cart/${action}.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: multipart ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: multipart ? payload : JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
      added = true;
      try { this.render(result.sections?.[this.dataset.sectionId]); }
      catch { await this.refresh(); }
      this.open();
    } catch (error) {
      if (added) { this.open(); this.error(this.dataset.addedError); }
      else throw error;
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.querySelectorAll('button').forEach(button => button.disabled = false);
    }
  }
}
if (!customElements.get('brewpic-cart-drawer')) customElements.define('brewpic-cart-drawer', BrewPicCartDrawer);
document.addEventListener('submit', async event => {
  const form = event.target.closest('[data-drawer-add-form]');
  if (!form) return;
  event.preventDefault();
  if (form.dataset.busy) return;
  let error = form.querySelector('[data-cart-error]');
  if (!error) { error = document.createElement('p'); error.setAttribute('role', 'alert'); form.append(error); }
  error.hidden = true;
  const button = form.querySelector('[type="submit"]');
  try {
    validatePhotos(form);
    if (!form.reportValidity()) return;
    const body = new FormData(form);
    body.delete('return_to');
    form.dataset.busy = 'true'; button.disabled = true;
    await document.querySelector('brewpic-cart-drawer').mutate('add', body);
  } catch (cause) { error.textContent = cause.message; error.hidden = false; }
  finally { delete form.dataset.busy; button.disabled = false; }
});
